import { Check, Loader2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface SaveStatusIndicatorProps {
  isSaving: boolean;
  hasUnsavedChanges?: boolean;
  saveFailed?: boolean;
}

type DisplayState = 'hidden' | 'unsaved' | 'saving' | 'saved' | 'fading';

export function SaveStatusIndicator({
  isSaving,
  hasUnsavedChanges = false,
  saveFailed = false
}: SaveStatusIndicatorProps) {
  const { t } = useTranslation();
  const [displayState, setDisplayState] = useState<DisplayState>(isSaving ? 'saving' : 'hidden');
  const prevIsSavingRef = useRef(isSaving);
  const [prevSaveFailed, setPrevSaveFailed] = useState(saveFailed);

  // A failure can be reported after the cycle already showed "Saved"
  if (saveFailed !== prevSaveFailed) {
    setPrevSaveFailed(saveFailed);
    if (saveFailed && (displayState === 'saved' || displayState === 'fading')) {
      setDisplayState('hidden');
    }
  }

  useEffect(
    function trackSaveTransitions() {
      const wasSaving = prevIsSavingRef.current;
      prevIsSavingRef.current = isSaving;
      // Started saving
      if (isSaving && !wasSaving) {
        setDisplayState('saving');
      }
      // Finished saving
      if (!isSaving && wasSaving) {
        setDisplayState(saveFailed ? 'hidden' : 'saved');
        let hideTimer: ReturnType<typeof setTimeout> | undefined;
        const fadeTimer = setTimeout(() => {
          setDisplayState((current) => (current === 'saved' ? 'fading' : current));
          hideTimer = setTimeout(() => setDisplayState('hidden'), 300);
        }, 2000);
        return () => {
          clearTimeout(fadeTimer);
          clearTimeout(hideTimer);
        };
      }
    },
    [isSaving, saveFailed]
  );

  // Show unsaved changes when not saving and there are changes
  useEffect(
    function syncUnsavedChangesState() {
      if (!isSaving && hasUnsavedChanges && displayState === 'hidden') {
        setDisplayState('unsaved');
      }
      if (!hasUnsavedChanges && displayState === 'unsaved') {
        setDisplayState('hidden');
      }
    },
    [hasUnsavedChanges, isSaving, displayState]
  );

  const isVisible = displayState !== 'hidden' && displayState !== 'fading';

  return (
    <div
      className={`flex items-center gap-2 min-w-[200px] justify-end transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
    >
      {displayState === 'unsaved' && (
        <>
          <div className="w-2 h-2 rounded-full bg-warning-solid" />
          <span className="text-sm text-warning">{t('processPage.unsavedChanges')}</span>
        </>
      )}
      {displayState === 'saving' && (
        <>
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t('processPage.Saving')}</span>
        </>
      )}
      {(displayState === 'saved' || displayState === 'fading') && (
        <>
          <Check className="w-4 h-4 text-positive" />
          <span className="text-sm text-positive">{t('processPage.Saved')}</span>
        </>
      )}
    </div>
  );
}
